import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {ActionBox} from 'shells/action-box/action-box';
import {ErrorIcon, SuccessIcon, WarningIcon} from 'shells/action-box/action-icons';
import {Button} from 'shells/button/button';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {getSubtitle, getTitle} from 'shells/prepare-to-send/helpers';
import {Body} from 'shells/typography/body';

import type {CampaignType} from 'nutshell-graphql-types';
import type {EditionFragment as Edition} from 'nutshell-graphql-types';

import {GetSessionUser} from '../../../core/get-session-user';

import {
    SubjectActionBox,
    UnsubscribeActionBox,
    StreetAddressActionBox,
    CustomClickDomainActionBox,
} from '../edition-action-boxes';
import type {ModalComponent} from '../edition-page';
import {AddressModal} from '../prepare-to-send-modal/onboarding-modals/address-modal';
import {UnsubscribeModal} from '../prepare-to-send-modal/onboarding-modals/unsubscribe-modal';

import {DKIMWarning} from './dkim-warning';
import type {EditionSecurityInfo} from './helpers';

import styles from './new-prepare-to-send-view.module.css';

type Props = {
    edition: Edition;
    streetAddress: string | null | undefined;
    closeAndMoveToView: (newView: string, onboardingModalComponent?: ModalComponent) => void;
    issueCount: number;
    type: CampaignType;
    isLoading: boolean;
    recipientCount: number | null | undefined;
    audienceUnsubcribedRate?: number | null | undefined;
    editionSecurityInfo: EditionSecurityInfo | null | undefined;
};

export function NewPrepareToSendView(props: Props) {
    const userFacingNoun = props.type === 'NEWSLETTER' ? 'edition' : 'broadcast';

    return (
        <div className={styles.container}>
            <div className='pad-16'>
                <StackLegacy spacing={8}>
                    <Body size='xlarge' isBold={true}>
                        {getTitle(props.issueCount, userFacingNoun)}
                    </Body>
                    <Body>{getSubtitle(props.issueCount, userFacingNoun)}</Body>
                </StackLegacy>
                <GetSessionUser>
                    {({user}) => {
                        return props.isLoading ? (
                            <div className='flex full-width full-height align-center justify-center'>
                                <LoadingIcon size={24} />
                            </div>
                        ) : (
                            <div className='py-16 full-width'>
                                <StackLegacy spacing={16}>
                                    {getFromSection(
                                        props.edition,
                                        () => props.closeAndMoveToView('details'),
                                        userFacingNoun,
                                        <ErrorIcon />,
                                        <SuccessIcon />,
                                        <WarningIcon />,
                                        Boolean(user && user.permissions.canAccessSetup),
                                        props.editionSecurityInfo
                                    )}
                                    <SubjectActionBox
                                        edition={props.edition}
                                        onClickActionItem={() => {
                                            props.closeAndMoveToView('details');
                                        }}
                                    />
                                    <UnsubscribeActionBox
                                        edition={props.edition}
                                        onClickActionItem={() => {
                                            props.closeAndMoveToView('editor', UnsubscribeModal);
                                        }}
                                    />
                                    <StreetAddressActionBox
                                        edition={props.edition}
                                        streetAddress={props.streetAddress}
                                        canAccessSetup={Boolean(
                                            user && user.permissions.canAccessSetup
                                        )}
                                        onClickActionItem={() => {
                                            props.closeAndMoveToView('editor', AddressModal);
                                        }}
                                    />
                                    {props.edition.customClickDomain &&
                                        !props.edition.customClickDomain.isVerified && (
                                            <CustomClickDomainActionBox
                                                canAccessSetup={Boolean(
                                                    user && user.permissions.canAccessSetup
                                                )}
                                            />
                                        )}
                                </StackLegacy>
                            </div>
                        );
                    }}
                </GetSessionUser>
                {getWarningBeforeSending(
                    props.edition,
                    props.recipientCount,
                    props.audienceUnsubcribedRate
                )}
            </div>
            <div className={styles['right-container']}>
                <img alt='' style={{width: 160}} src='/include/images/empty-states/broadcast.svg' />
            </div>
        </div>
    );
}

function getFromSection(
    edition: Edition,
    onGoToDetails: () => void,
    userFacingNoun: string,
    exMark: React.ReactElement<any>,
    checkmark: React.ReactElement<any>,
    warning: React.ReactElement<any>,
    canAccessSetup: boolean,
    editionSecurityInfo?: EditionSecurityInfo | null
) {
    if (!edition.from) {
        // Just don't have a from
        return (
            <ActionBox
                size='large'
                title='From'
                subtitle='Select a sender for this message'
                actionElement={
                    <Button
                        variant='text-primary'
                        size='big'
                        onClick={() => {
                            onGoToDetails();
                        }}
                    >
                        Go to {userFacingNoun} details
                    </Button>
                }
                icon={exMark}
            />
        );
    } else if (edition.from && edition.from.status !== 'VERIFIED') {
        // They have a from, but it’s still unverified (only admins can access setup)
        // TODO: Update this when non-admins can access marketing settings
        return (
            <ActionBox
                size='large'
                title='From'
                subtitle='Your selected sender address hasn’t been verified'
                actionElement={
                    canAccessSetup ? (
                        <Link
                            variant='primary'
                            to='/email-marketing/sender-address'
                            // @ts-expect-error
                            as={RoutedLink}
                        >
                            Manage sender addresses
                        </Link>
                    ) : undefined
                }
                icon={exMark}
            />
        );
    } else if (edition.from) {
        return (
            <DKIMWarning
                editionFrom={edition.from}
                canAccessSetup={canAccessSetup}
                userFacingNoun={userFacingNoun}
                editionSecurityInfo={editionSecurityInfo}
            />
        );
    }
}

function getWarningBeforeSending(
    edition: Edition,
    audienceCount?: number | null,
    audienceUnsubcribedRate?: number | null
): React.ReactElement<any> | null | undefined {
    if (
        audienceCount &&
        audienceCount > 500 &&
        typeof audienceUnsubcribedRate === 'number' &&
        audienceUnsubcribedRate < 0.01
    ) {
        return (
            <FlashBanner type='warning' hasBorder={true} noRightPadding={true}>
                <div className='flex justify-sb align-center'>
                    <div style={{maxWidth: '75%'}}>
                        <span style={{fontWeight: 'bold'}}>Heads up!</span> We recommend importing
                        unsubscribers into Nutshell before sending this broadcast, or your message
                        might result in a high bounce rate.
                    </div>
                    <Link
                        variant='primary'
                        href='https://support.nutshell.com/en/articles/8428874-audiences-unsubscribe-contacts-from-campaigns'
                        newTab={true}
                    >
                        Learn more
                    </Link>
                </div>
            </FlashBanner>
        );
    }

    if (edition.hasWebFonts) {
        return (
            <FlashBanner type='info' hasBorder={true} noRightPadding={true}>
                <div className='flex justify-sb full-width'>
                    This message uses a web font that may appear differently in some inboxes
                    <Link
                        variant='primary'
                        href='https://support.nutshell.com/en/articles/8428876-campaigns-which-fonts-can-you-use'
                        newTab={true}
                    >
                        Learn more
                    </Link>
                </div>
            </FlashBanner>
        );
    }

    return null;
}
